import asyncDebounce from "awesome-debounce-promise";
import { AuthStore } from "../stores/AuthStore";
import RootStore from "../stores/RootStore";
import { fetch_retry } from "../utils/FetchRetry";

export interface ValidationProblemDetails {
    status: number;
    title: string;
    errors: Record<string, string[]>;
}

export class ServiceError extends Error {
    public url: string;
    public response: Response;
    public errorMessage?: string;
    public errorJson?: any;

    public constructor(
        requestType: string,
        url: string,
        response: Response,
        errorMessage?: string,
        errorJson?: any,
    ) {
        const message = `${requestType} to ${url} Failed: ${response.statusText} ${errorMessage}`;
        super(message);

        try {
            const responseError = JSON.parse(errorMessage ?? "");
            this.errorMessage = responseError.message;
            this.errorJson = errorJson ?? {};

            if (
                response.headers.get("Acx-Mfa-Required")?.toLowerCase() ===
                    "true" &&
                window.location.pathname !== "/mfa-required"
            ) {
                const origin = window.location.href;
                const targetOrgId =
                    response.headers.get("Acx-Mfa-OrganizationId") ??
                    responseError.organizationId ??
                    RootStore().getStore(AuthStore).getRequestHeader()[
                        "Acx-OrganizationId"
                    ];

                if (targetOrgId) {
                    sessionStorage.setItem("pendingMfaTargetOrgId", targetOrgId);
                }

                window.location.href = `/mfa-required?targetOrgId=${targetOrgId}&returnUrl=${encodeURIComponent(origin)}`;
            }

            if (this.errorMessage?.includes("expired")) {
                const origin = window.location.href;

                window.location.href = `/authentication/login?returnUrl=${encodeURIComponent(
                    origin,
                )}`;
            }
        } catch (e) {
            this.errorMessage = errorMessage ?? "Something went wrong";
            this.errorJson = errorJson ?? {};
        }

        this.url = url;
        this.response = response;
    }

    private responseJson?: any;
    private async getResponseJson(): Promise<any> {
        if (!this.responseJson) {
            try {
                this.responseJson = await this.response.json();
            } catch {}
        }

        return this.responseJson;
    }

    /**
     * Inspect the body, as json, of the corresponding response to see
     * if it matches `ValidationProblemDetails`. Namely, if the json has a
     * `title` and `errors` property and the `errors` property is an object
     * with `string` keys and list of `string` values.
     *
     * @see https://coda.io/d/_dcOWiKxpXlL/Code-Standards_su1ZfNGf#_lub-iU8P
     */
    public async isValidationProblem() {
        const json = await this.getResponseJson();
        return (
            typeof json === "object" &&
            "title" in json &&
            "errors" in json &&
            typeof json["errors"] === "object" &&
            Object.values(json["errors"]).every(
                (value) =>
                    Array.isArray(value) &&
                    value.every((error) => typeof error === "string"),
            )
        );
    }

    /**
     * Coerces the response json to a `ValidationProblemDetails` object. Some endpoints return
     * this when there is an error validating the request body sent to them.
     *
     * You may call `isValidationProblem()` to check if this `ServiceError` is a validation problem itself.
     *
     * @returns {ValidationProblemDetails}
     * @see https://coda.io/d/_dcOWiKxpXlL/Code-Standards_su1ZfNGf#_lub-iU8P
     */
    public async asValidationProblemDetails(): Promise<ValidationProblemDetails> {
        if (!(await this.isValidationProblem())) {
            throw new Error(
                "Error response was not validation problem details",
            );
        }

        const json = await this.getResponseJson();
        return json as ValidationProblemDetails;
    }
}

export abstract class BaseService {
    protected abstract urlBase: string;
    protected apiVersion: string = "1.0";

    //authFailureHandler is wired up in the RootStore
    //handler is found in AuthStore
    constructor(public authFailureHandler?: () => Promise<void>) {}

    protected splitUrlParams = (
        originalUrl: string,
        existingParams: URLSearchParams,
    ) => {
        const embeddedParams = originalUrl.split("?");
        if (embeddedParams.length > 1) {
            embeddedParams[1]
                .split("&")
                .forEach((param) =>
                    existingParams.append(
                        param.split("=")[0],
                        param.split("=")[1],
                    ),
                );
        }
        return { basePath: embeddedParams[0], queryParams: existingParams };
    };

    protected post: <T = any>(
        body: URLSearchParams | string | any | null | FormData,
        urlParams: URLSearchParams,
        url: string,
        httpOptions?: { returnJson?: boolean },
        debounceKey?: string,
        abortSignal?: AbortSignal,
        asRawResponse?: boolean,
    ) => Promise<T> = asyncDebounce(
        async <T = any>(
            body: URLSearchParams | string | any | null,
            urlParams: URLSearchParams,
            url: string,
            httpOptions?: { returnJson?: boolean },
            debounceKey?: string,
            abortSignal?: AbortSignal,
            asRawResponse?: boolean,
        ): Promise<T> => {
            let headers = RootStore().getStore(AuthStore).getRequestHeader();
            headers = { ...headers, "api-version": this.apiVersion };
            // Let the browser determine the content type based on the FormData body
            if (body instanceof FormData) {
                delete headers["Content-Type"];
            }
            const options = {
                method: "POST",
                headers: headers,
                body: body,
            };
            if (abortSignal) {
                options["signal"] = abortSignal;
            }
            const response = await fetch_retry(
                `/${this.urlBase}/${url}?${urlParams}`,
                options,
                2,
            );

            const clonedResp = response.clone();
            if (!response.ok) {
                if (response.status === 401) {
                    await this.authFailureHandler?.();
                }

                const returnJson = httpOptions?.returnJson && response.json;
                const errorData = returnJson
                    ? await response.json().catch((err) => {
                          return clonedResp.text();
                      })
                    : await response.text();

                throw new ServiceError(
                    "HttpPost",
                    url,
                    clonedResp,
                    !returnJson && errorData,
                    returnJson && errorData,
                );
            }

            if (asRawResponse) return response as T;
            const resp = await response.json().catch(() => {});
            return resp;
        },
        255,
        {
            key: (
                body: URLSearchParams | string | any | null,
                urlParams: URLSearchParams,
                url: string,
                httpOptions?: { returnJson?: boolean },
                debounceKey?: string,
            ) =>
                `HttpPost-${this.urlBase}-${
                    debounceKey ?? `${url}-${urlParams?.toString()}`
                }`,
        },
    );

    /**
     * GET without debouncing. Use when each caller must own the response,
     * such as a raw response whose body stream can only be read once.
     */
    protected getUndebounced = async <T = any>(
        urlParams: URLSearchParams,
        url: string,
        abortSignal?: AbortSignal,
        asRawResponse?: boolean,
    ): Promise<T> => {
        let headers = RootStore().getStore(AuthStore).getRequestHeader();
        headers = { ...headers, "api-version": this.apiVersion };
        const options = {
            method: "GET",
            headers: headers,
        };
        if (abortSignal) {
            options["signal"] = abortSignal;
        }
        const response = await fetch_retry(
            `/${this.urlBase}/${url}?${urlParams}`,
            options,
            2,
        );
        const clonedResp = response.clone();
        if (!response.ok) {
            if (response.status === 401) {
                await this.authFailureHandler?.();
            }

            throw new ServiceError(
                "HttpGet",
                `${this.urlBase}/${url}`,
                clonedResp,
                await response.text(),
            );
        }

        if (asRawResponse) return response as T;
        return response.json();
    };

    protected get: <T = any>(
        urlParams: URLSearchParams,
        url: string,
        abortSignal?: AbortSignal,
        debounceKey?: string,
        asRawResponse?: boolean,
    ) => Promise<T> = asyncDebounce(
        async <T = any>(
            urlParams: URLSearchParams,
            url: string,
            abortSignal?: AbortSignal,
            debounceKey?: string,
            asRawResponse?: boolean,
        ): Promise<T> =>
            this.getUndebounced<T>(urlParams, url, abortSignal, asRawResponse),
        255,
        {
            key: (
                urlParams: URLSearchParams,
                url: string,
                abortSignal?: AbortSignal,
                debounceKey?: string,
                asRawResponse?: boolean,
            ) => {
                return `HttpGet-${this.urlBase}-${
                    debounceKey ?? `${url}-${urlParams?.toString()}`
                }`;
            },
        },
    );

    protected delete: (
        urlParams: URLSearchParams,
        url: string,
        body?: URLSearchParams | string | any | null,
        httpOptions?: { returnJson?: boolean },
        debounceKey?: string,
    ) => Promise<Response> = asyncDebounce(
        async (
            urlParams: URLSearchParams,
            url: string,
            body?: URLSearchParams | string | any | null,
            httpOptions?: { returnJson?: boolean },
            debounceKey?: string,
        ) => {
            let headers = RootStore().getStore(AuthStore).getRequestHeader();
            headers = { ...headers, "api-version": this.apiVersion };
            const options = {
                method: "DELETE",
                headers: headers,
                body: body,
            };
            const response = await fetch_retry(
                `/${this.urlBase}/${url}?${urlParams}`,
                options,
                2,
            );
            const clonedResp = response.clone();
            if (!response.ok) {
                if (response.status === 401) {
                    await this.authFailureHandler?.();
                }

                const returnJson = httpOptions?.returnJson && response.json;
                const errorData = returnJson
                    ? await response.json()
                    : await response.text();

                throw new ServiceError(
                    "HttpDelete",
                    `${this.urlBase}/${url}`,
                    clonedResp,
                    !returnJson && errorData,
                    returnJson && errorData,
                );
            }

            return response;
        },
        255,
        {
            key: (
                urlParams: URLSearchParams,
                url: string,
                body?: URLSearchParams | string | any | null,
                debounceKey?: string,
            ) =>
                `HttpDelete-${this.urlBase}-${
                    debounceKey ?? `${url}-${urlParams?.toString()}`
                }`,
        },
    );

    protected put: <T = any>(
        urlParams: URLSearchParams,
        url: string,
        body?: URLSearchParams | string | any | null,
        httpOptions?: { returnJson?: boolean },
        debounceKey?: string,
    ) => Promise<T> = asyncDebounce(
        async <T = any>(
            urlParams: URLSearchParams,
            url: string,
            body?: URLSearchParams | string | null,
            httpOptions?: { returnJson?: boolean },
            debounceKey?: string,
        ): Promise<T> => {
            let headers = RootStore().getStore(AuthStore).getRequestHeader();
            headers = { ...headers, "api-version": this.apiVersion };
            const options = {
                method: "PUT",
                headers: headers,
                body: body,
            };
            const response = await fetch_retry(
                `/${this.urlBase}/${url}?${urlParams}`,
                options,
                2,
            );
            const clonedResp = response.clone();
            if (!response.ok) {
                if (response.status === 401) {
                    await this.authFailureHandler?.();
                }
                const returnJson = httpOptions?.returnJson && response.json;
                const errorData = returnJson
                    ? await response.json()
                    : await response.text();

                throw new ServiceError(
                    "HttpPut",
                    `${this.urlBase}/${url}`,
                    clonedResp,
                    !returnJson && errorData,
                    returnJson && errorData,
                );
            }

            return response.json().catch(() => {});
        },
        255,
        {
            key: (
                urlParams: URLSearchParams,
                url: string,
                body?: URLSearchParams | string | null,
                httpOptions?: { returnJson?: boolean },
                debounceKey?: string,
            ) =>
                `HttPut-${this.urlBase}-${
                    debounceKey ?? `${url}-${urlParams?.toString()}`
                }`,
        },
    );

    protected patch: <T = any>(
        body: URLSearchParams | string | any | null,
        urlParams: URLSearchParams,
        url: string,
        httpOptions?: { returnJson?: boolean },
        debounceKey?: string,
        abortSignal?: AbortSignal,
        addtlOptions?: RequestInit,
        asRawResponse?: boolean,
    ) => Promise<T> = asyncDebounce(
        async <T = any>(
            body: URLSearchParams | string | any | null,
            urlParams: URLSearchParams,
            url: string,
            httpOptions?: { returnJson?: boolean },
            debounceKey?: string,
            abortSignal?: AbortSignal,
            addtlOptions?: RequestInit,
            asRawResponse?: boolean,
        ): Promise<T> => {
            let headers = RootStore().getStore(AuthStore).getRequestHeader();
            headers = { ...headers, "api-version": this.apiVersion };
            const options = {
                ...addtlOptions,
                method: "PATCH",
                headers: headers,
                body: body,
            };
            if (abortSignal) {
                options["signal"] = abortSignal;
            }
            const response = await fetch_retry(
                `/${this.urlBase}/${url}?${urlParams}`,
                options,
                2,
            );
            const clonedResp = response.clone();
            if (!response.ok) {
                if (response.status === 401) {
                    await this.authFailureHandler?.();
                }

                const returnJson = httpOptions?.returnJson && response.json;
                const errorData = returnJson
                    ? await response.json().catch((err) => {
                          return clonedResp.text();
                      })
                    : await response.text();

                throw new ServiceError(
                    "HttpPatch",
                    url,
                    clonedResp,
                    !returnJson && errorData,
                    returnJson && errorData,
                );
            }

            if (asRawResponse) return response as T;
            return response.json().catch(() => {});
        },
        255,
        {
            key: (
                body: URLSearchParams | string | any | null,
                urlParams: URLSearchParams,
                url: string,
                httpOptions?: { returnJson?: boolean },
                debounceKey?: string,
            ) =>
                `HttpPatch-${this.urlBase}-${
                    debounceKey ?? `${url}-${urlParams?.toString()}`
                }`,
        },
    );

    protected getArrayBuffer: (
        urlParams: URLSearchParams,
        url: string,
        debounceKey?: string,
    ) => Promise<ArrayBuffer> = asyncDebounce(
        async (
            urlParams: URLSearchParams,
            url: string,
            debounceKey?: string,
        ): Promise<ArrayBuffer> => {
            const headers = RootStore().getStore(AuthStore).getRequestHeader();
            const options = {
                method: "GET",
                headers: headers,
            };
            const response = await fetch_retry(
                `/${this.urlBase}/${url}?${urlParams}`,
                options,
                2,
            );
            const clonedResp = response.clone();
            if (!response.ok) {
                if (response.status === 401) {
                    await this.authFailureHandler?.();
                }

                throw new ServiceError(
                    "HttpGet ArrayBuffer",
                    `${this.urlBase}/${url}`,
                    clonedResp,
                    await response.text(),
                );
            }

            return response.arrayBuffer();
        },
        255,
        {
            key: (
                urlParams: URLSearchParams,
                url: string,
                debounceKey?: string,
            ) =>
                `ArrayBuffer-${this.urlBase}-${
                    debounceKey ?? `${url}-${urlParams?.toString()}`
                }`,
        },
    );

    protected getBlob: (
        urlParams: URLSearchParams,
        url: string,
        debounceKey?: string,
    ) => Promise<Blob> = asyncDebounce(
        async (
            urlParams: URLSearchParams,
            url: string,
            debounceKey?: string,
        ): Promise<Blob> => {
            const headers = RootStore().getStore(AuthStore).getRequestHeader();
            const options = {
                method: "GET",
                headers: headers,
            };
            const response = await fetch_retry(
                `/${this.urlBase}/${url}?${urlParams}`,
                options,
                2,
            );
            const clonedResp = response.clone();
            if (!response.ok) {
                if (response.status === 401) {
                    await this.authFailureHandler?.();
                }

                throw new ServiceError(
                    "HttpGet Blob",
                    `${this.urlBase}/${url}`,
                    clonedResp,
                    await response.text(),
                );
            }

            return response.blob();
        },
        255,
        {
            key: (
                urlParams: URLSearchParams,
                url: string,
                debounceKey?: string,
            ) =>
                `Blob-${this.urlBase}-${
                    debounceKey ?? `${url}-${urlParams?.toString()}`
                }`,
        },
    );
}

export default BaseService;
